import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtUSD, fmtDate } from "../lib/format";
import { PageHeader, StatusBadge } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Sparkles, Loader2, Copy } from "lucide-react";

const DTYPES = ["Individual", "Corporate", "Foundation"];

export default function Donors() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [ackText, setAckText] = useState("");
  const [ackBusy, setAckBusy] = useState(false);
  const [form, setForm] = useState({ donor_type: "Individual", first_name: "", last_name: "", organization_name: "", email: "", phone: "" });

  const load = () => api.get("/donors").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/donors", form);
      toast.success("Donor added");
      setOpen(false);
      setForm({ donor_type: "Individual", first_name: "", last_name: "", organization_name: "", email: "", phone: "" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const openDetail = async (d) => {
    setAckText("");
    const { data } = await api.get(`/donors/${d.id}`);
    setDetail(data);
  };

  const genAck = async () => {
    setAckBusy(true); setAckText("");
    try {
      const last = detail.gifts?.[0]?.amount || detail.lifetime_giving || 0;
      const { data } = await api.post("/ai/donor-acknowledgment", { donor_id: detail.id, gift_amount: last, tone: "warm" });
      setAckText(data.result);
    } catch (err) { toast.error(apiError(err)); } finally { setAckBusy(false); }
  };

  if (!rows) return <Loader />;

  const columns = [
    { key: "name", label: "Donor", render: (r) => <span className="font-medium">{r.organization_name || `${r.first_name} ${r.last_name}`}</span> },
    { key: "donor_type", label: "Type", render: (r) => <StatusBadge tone="blue">{r.donor_type}</StatusBadge> },
    { key: "email", label: "Email", className: "text-[#5C5C54]" },
    { key: "gift_count", label: "Gifts", align: "right" },
    { key: "lifetime_giving", label: "Lifetime Giving", align: "right", render: (r) => <span className="font-semibold text-inflow">{fmtUSD(r.lifetime_giving)}</span> },
  ];

  return (
    <div>
      <PageHeader title="Donor Master" subtitle="Steward relationships and giving history. Click a donor for details & AI acknowledgments."
        action={<AddButton onClick={() => setOpen(true)} label="New Donor" testid="add-donor-btn" />} />
      <DataTable columns={columns} rows={rows} testid="donors-table" onRowClick={openDetail} />

      <Modal open={open} onOpenChange={setOpen} title="New Donor" onSubmit={submit} busy={busy} testid="donor-modal">
        <Field label="Donor Type" span={2}><SelectInput value={form.donor_type} onChange={(e) => setForm({ ...form, donor_type: e.target.value })} options={DTYPES} /></Field>
        {form.donor_type === "Individual" ? (
          <>
            <Field label="First Name"><TextInput value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
            <Field label="Last Name"><TextInput value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Field>
          </>
        ) : (
          <Field label="Organization Name" span={2}><TextInput value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} /></Field>
        )}
        <Field label="Email"><TextInput type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        <Field label="Phone"><TextInput value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
      </Modal>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="bg-white w-full sm:max-w-lg overflow-y-auto">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading text-2xl">{detail.organization_name || `${detail.first_name} ${detail.last_name}`}</SheetTitle>
                <SheetDescription className="sr-only">Donor details and giving history</SheetDescription>
              </SheetHeader>
              <div className="mt-4 flex items-center gap-3">
                <StatusBadge tone="blue">{detail.donor_type}</StatusBadge>
                <span className="text-sm text-[#5C5C54]">{detail.email}</span>
              </div>
              <div className="mt-6 bg-stoney border border-stone-200 rounded-xl p-5">
                <div className="text-xs uppercase tracking-wider text-[#5C5C54]">Lifetime Giving</div>
                <div className="font-mono text-2xl font-semibold mt-1">{fmtUSD(detail.lifetime_giving)}</div>
              </div>

              <button onClick={genAck} disabled={ackBusy} data-testid="gen-ack-btn"
                className="mt-6 w-full bg-forest hover:bg-forest-900 text-white rounded-md px-4 py-2.5 text-sm font-medium transition-colors duration-200 flex items-center justify-center disabled:opacity-60">
                {ackBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Draft AI Thank-You Letter
              </button>

              {ackText && (
                <div className="mt-4 relative">
                  <button onClick={() => { navigator.clipboard.writeText(ackText); toast.success("Copied"); }}
                    className="absolute top-2 right-2 text-[#5C5C54] hover:text-forest"><Copy className="h-4 w-4" /></button>
                  <pre className="whitespace-pre-wrap text-sm bg-white border border-stone-200 rounded-lg p-4 font-sans text-[#1A1A1A]" data-testid="ack-result">{ackText}</pre>
                </div>
              )}

              <h3 className="text-lg font-medium mt-8 mb-3">Giving History</h3>
              <div className="space-y-2">
                {(detail.gifts || []).length === 0 && <p className="text-sm text-[#5C5C54]">No gifts recorded yet.</p>}
                {(detail.gifts || []).map((g) => (
                  <div key={g.id} className="flex items-center justify-between border-b border-stone-100 py-2">
                    <div>
                      <div className="text-sm">{g.revenue_type}</div>
                      <div className="text-xs text-[#5C5C54]">{fmtDate(g.revenue_date)}</div>
                    </div>
                    <div className="font-mono font-semibold text-inflow">{fmtUSD(g.amount)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
